import React from 'react';
import { IPresenterProps, IUserBooking } from './types';
import * as S from './styles';
import NavBar2 from '../../components/NavBar2/Container';
import { Calendar, Clock, LogOut, User, Settings, X } from 'lucide-react';
import { BOOKING_STATUS } from '../../utils/constants';

const getStatusLabel = (status: string): string => {
    switch (status) {
        case BOOKING_STATUS.PENDING:
            return 'Pendiente';
        case BOOKING_STATUS.CONFIRMED:
            return 'Confirmada';
        case BOOKING_STATUS.COMPLETED:
            return 'Completada';
        case BOOKING_STATUS.CANCELLED:
            return 'Cancelada';
        case BOOKING_STATUS.NO_SHOW:
            return 'No asistio';
        case 'PENDING_PAYMENT':
            return 'Pago pendiente';
        default:
            return status;
    }
};

const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

const getInitials = (email: string): string => {
    const name = email.split('@')[0];
    return name.substring(0, 2).toUpperCase();
};

const UserProfilePresenter = (props: IPresenterProps) => {
    const {
        authUser,
        isLoading,
        upcomingBookings,
        pastBookings,
        onLogout,
        onCancelBooking,
        onBookAgain,
        cancelModalOpen,
        bookingToCancel,
        onCloseCancelModal,
        onConfirmCancel,
        isCancelling
    } = props;

    const renderBookingCard = (booking: IUserBooking, isUpcoming: boolean) => {
        const servicesText = booking.services
            ?.map(s => s.name)
            .join(', ') || 'Servicio';

        return (
            <S.BookingCard key={booking.id}>
                <S.BookingHeader>
                    <S.BusinessName>{booking.business?.name || 'Negocio'}</S.BusinessName>
                    <S.StatusBadge $status={booking.status}>
                        {getStatusLabel(booking.status)}
                    </S.StatusBadge>
                </S.BookingHeader>
                <S.BookingDetails>
                    <S.BookingDateTime>
                        <Calendar size={16} />
                        {booking.startTimestamp
                            ? formatDate(booking.startTimestamp)
                            : 'Fecha no disponible'}
                    </S.BookingDateTime>
                    <S.BookingDateTime>
                        <Clock size={16} />
                        {booking.selectedTime?.startTime || 'Hora no disponible'}
                    </S.BookingDateTime>
                    <S.BookingServices>
                        {servicesText}
                        {booking.totalCost && ` - $${booking.totalCost}`}
                    </S.BookingServices>
                </S.BookingDetails>
                <S.BookingActions>
                    {isUpcoming && (booking.status === BOOKING_STATUS.PENDING ||
                        booking.status === BOOKING_STATUS.CONFIRMED) && (
                        <S.ActionButton
                            $variant="danger"
                            onClick={() => {
                                console.log('=== CANCELAR BUTTON CLICKED ===');
                                console.log('booking:', booking);
                                console.log('booking.id:', booking.id);
                                if (booking.id) {
                                    onCancelBooking(booking.id);
                                } else {
                                    console.error('NO BOOKING ID!');
                                    alert('Error: No se encontró el ID de la reserva');
                                }
                            }}
                        >
                            Cancelar
                        </S.ActionButton>
                    )}
                    {!isUpcoming && booking.status === BOOKING_STATUS.COMPLETED && (
                        <S.ActionButton
                            $variant="primary"
                            onClick={() => onBookAgain(booking)}
                        >
                            Reservar de nuevo
                        </S.ActionButton>
                    )}
                </S.BookingActions>
            </S.BookingCard>
        );
    };

    const renderCancelModal = () => {
        if (!cancelModalOpen || !bookingToCancel) return null;

        const servicesText = bookingToCancel.services
            ?.map(s => s.name)
            .join(', ') || 'Servicio';

        return (
            <S.ModalOverlay onClick={onCloseCancelModal}>
                <S.ModalContent onClick={e => e.stopPropagation()}>
                    <S.ModalHeader>
                        <S.ModalTitle>Cancelar Reserva</S.ModalTitle>
                        <S.ModalCloseButton onClick={onCloseCancelModal}>
                            <X size={20} />
                        </S.ModalCloseButton>
                    </S.ModalHeader>
                    <S.ModalBody>
                        <S.ModalText>
                            ¿Estás seguro que deseas cancelar esta reserva?
                        </S.ModalText>
                        <S.ModalBookingInfo>
                            <S.ModalBookingDetail>
                                <strong>{bookingToCancel.business?.name || 'Negocio'}</strong>
                            </S.ModalBookingDetail>
                            <S.ModalBookingDetail>
                                {bookingToCancel.startTimestamp
                                    ? formatDate(bookingToCancel.startTimestamp)
                                    : 'Fecha no disponible'}
                            </S.ModalBookingDetail>
                            <S.ModalBookingDetail>
                                {bookingToCancel.selectedTime?.startTime || 'Hora no disponible'}
                            </S.ModalBookingDetail>
                            <S.ModalBookingDetail>
                                {servicesText}
                            </S.ModalBookingDetail>
                        </S.ModalBookingInfo>
                    </S.ModalBody>
                    <S.ModalFooter>
                        <S.ModalButton $variant="secondary" onClick={onCloseCancelModal}>
                            Volver
                        </S.ModalButton>
                        <S.ModalButton 
                            $variant="danger" 
                            onClick={() => {
                                console.log('=== CONFIRM BUTTON CLICKED ===');
                                onConfirmCancel();
                            }}
                            disabled={isCancelling}
                        >
                            {isCancelling ? 'Cancelando...' : 'Confirmar Cancelación'}
                        </S.ModalButton>
                    </S.ModalFooter>
                </S.ModalContent>
            </S.ModalOverlay>
        );
    };

    if (isLoading) {
        return (
            <>
                <NavBar2 isSearchBarHidden={true} />
                <S.PageContainer>
                    <S.Container>
                        <S.LoadingContainer>
                            Cargando...
                        </S.LoadingContainer>
                    </S.Container>
                </S.PageContainer>
            </>
        );
    }

    return (
        <>
            <NavBar2 isSearchBarHidden={true} />
            <S.PageContainer>
                <S.Container>
                    <S.ProfileHeader>
                        <S.Avatar>
                            {authUser?.email ? getInitials(authUser.email) : <User size={32} />}
                        </S.Avatar>
                        <S.UserInfo>
                            <S.UserEmail>{authUser?.email || 'Usuario'}</S.UserEmail>
                            <S.MemberSince>
                                Bienvenido a Mora
                            </S.MemberSince>
                        </S.UserInfo>
                    </S.ProfileHeader>

                    <S.Section>
                        <S.SectionTitle>Proximas Reservas</S.SectionTitle>
                        {upcomingBookings.length > 0 ? (
                            upcomingBookings.map(booking => renderBookingCard(booking, true))
                        ) : (
                            <S.EmptyState>
                                <S.EmptyStateText>
                                    No tienes reservas proximas
                                </S.EmptyStateText>
                            </S.EmptyState>
                        )}
                    </S.Section>

                    <S.Section>
                        <S.SectionTitle>Historial de Reservas</S.SectionTitle>
                        {pastBookings.length > 0 ? (
                            pastBookings.map(booking => renderBookingCard(booking, false))
                        ) : (
                            <S.EmptyState>
                                <S.EmptyStateText>
                                    No tienes reservas anteriores
                                </S.EmptyStateText>
                            </S.EmptyState>
                        )}
                    </S.Section>

                    <S.Section>
                        <S.SectionTitle>Configuracion de Cuenta</S.SectionTitle>
                        <S.SettingsSection>
                            <S.SettingsItem>
                                <Settings size={18} />
                                Cambiar contrasena
                            </S.SettingsItem>
                            <S.Separator />
                            <S.SettingsItemDanger onClick={onLogout}>
                                <LogOut size={18} />
                                Cerrar sesion
                            </S.SettingsItemDanger>
                        </S.SettingsSection>
                    </S.Section>
                </S.Container>
            </S.PageContainer>
            
            {renderCancelModal()}
        </>
    );
};

export default UserProfilePresenter;
